
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Pencil, Trash2, Plus, Check, X } from "lucide-react";
import { AdminLayout } from "@/components/admin/AdminLayout";

interface Region {
  id: number;
  code: string;
  name: string;
  active: boolean;
  timezone: string;
  currency: string;
}

const ManageRegions = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editingRegion, setEditingRegion] = useState<Region | null>(null);
  const [regionForm, setRegionForm] = useState({
    code: "",
    name: "",
    timezone: "Africa/Nairobi",
    currency: "Ksh"
  });

  const { data: regions } = useQuery({
    queryKey: ["regions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("regions")
        .select("*")
        .order("name");
      
      if (error) throw error;
      return data as Region[];
    },
  });

  const addRegionMutation = useMutation({
    mutationFn: async (data: { 
      code: string; 
      name: string;
      timezone: string;
      currency: string;
    }) => {
      const { error } = await supabase
        .from("regions")
        .insert([{ 
          code: data.code.toLowerCase(), 
          name: data.name,
          timezone: data.timezone,
          currency: data.currency
        }]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["regions"] });
      toast({ title: "Success", description: "Region added successfully" });
      setRegionForm({ code: "", name: "", timezone: "Africa/Nairobi", currency: "Ksh" });
      setIsOpen(false);
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const updateRegionMutation = useMutation({
    mutationFn: async (region: Region) => {
      const { error } = await supabase
        .from("regions")
        .update({ 
          name: region.name, 
          code: region.code.toLowerCase(),
          timezone: region.timezone,
          currency: region.currency
        })
        .eq("id", region.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["regions"] });
      toast({ title: "Success", description: "Region updated successfully" });
      setEditingRegion(null);
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const toggleRegionStatusMutation = useMutation({
    mutationFn: async (region: Region) => {
      const { error } = await supabase
        .from("regions")
        .update({ active: !region.active })
        .eq("id", region.id);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["regions"] });
      toast({
        title: "Success",
        description: `Region ${variables.name} ${variables.active ? 'deactivated' : 'activated'} successfully`,
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const deleteRegionMutation = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await supabase
        .from("regions")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["regions"] });
      toast({ title: "Success", description: "Region deleted successfully" });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!regionForm.code || !regionForm.name || !regionForm.timezone || !regionForm.currency) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please fill in all fields",
      });
      return;
    }

    if (editingRegion) {
      updateRegionMutation.mutate({
        ...editingRegion,
        code: regionForm.code,
        name: regionForm.name,
        timezone: regionForm.timezone,
        currency: regionForm.currency
      });
    } else {
      addRegionMutation.mutate(regionForm);
    }
  };

  const openEditDialog = (region: Region) => {
    setEditingRegion(region);
    setRegionForm({
      code: region.code,
      name: region.name,
      timezone: region.timezone || "Africa/Nairobi",
      currency: region.currency || "Ksh"
    });
    setIsOpen(true);
  };

  return (
    <AdminLayout title="Manage Regions">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Regions</h2>
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => {
                setEditingRegion(null);
                setRegionForm({ code: "", name: "", timezone: "Africa/Nairobi", currency: "Ksh" });
              }}>
                <Plus className="w-4 h-4 mr-2" />
                Add New Region
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingRegion ? "Edit Region" : "Add New Region"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 py-4">
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <label htmlFor="code" className="text-sm font-medium">
                      Region Code
                    </label>
                    <Input
                      id="code"
                      placeholder="e.g., nairobi"
                      value={regionForm.code}
                      onChange={(e) =>
                        setRegionForm({ ...regionForm, code: e.target.value })
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <label htmlFor="name" className="text-sm font-medium">
                      Region Name
                    </label>
                    <Input
                      id="name"
                      placeholder="e.g., Nairobi"
                      value={regionForm.name}
                      onChange={(e) =>
                        setRegionForm({ ...regionForm, name: e.target.value })
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <label htmlFor="timezone" className="text-sm font-medium">
                      Timezone
                    </label>
                    <Input
                      id="timezone"
                      placeholder="e.g., Africa/Nairobi"
                      value={regionForm.timezone}
                      onChange={(e) =>
                        setRegionForm({ ...regionForm, timezone: e.target.value })
                      }
                    />
                    <p className="text-xs text-gray-500">Format: Africa/Nairobi, Europe/London, etc.</p>
                  </div>
                  <div className="grid gap-2">
                    <label htmlFor="currency" className="text-sm font-medium">
                      Currency Code
                    </label>
                    <Input
                      id="currency"
                      placeholder="e.g., Ksh"
                      value={regionForm.currency}
                      onChange={(e) =>
                        setRegionForm({ ...regionForm, currency: e.target.value })
                      }
                    />
                    <p className="text-xs text-gray-500">Examples: Ksh, USD, EUR, GBP</p>
                  </div>
                  <Button type="submit">
                    {editingRegion ? "Update Region" : "Add Region"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Currency</TableHead>
              <TableHead>Timezone</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[150px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {regions?.map((region) => (
              <TableRow key={region.id} className="hover:bg-gray-50">
                <TableCell className="font-medium">{region.code}</TableCell>
                <TableCell>{region.name}</TableCell>
                <TableCell>{region.currency}</TableCell>
                <TableCell>{region.timezone}</TableCell>
                <TableCell>
                  {region.active ? (
                    <span className="flex items-center text-green-600">
                      <Check className="h-4 w-4 mr-1" /> Active
                    </span>
                  ) : (
                    <span className="flex items-center text-red-600">
                      <X className="h-4 w-4 mr-1" /> Inactive
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(region)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={region.active ? "destructive" : "outline"}
                      size="sm"
                      onClick={() => toggleRegionStatusMutation.mutate(region)}
                    >
                      {region.active ? "Deactivate" : "Activate"}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteRegionMutation.mutate(region.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!regions?.length && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                  No regions found. Click "Add New Region" to create one.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </AdminLayout>
  );
};

export default ManageRegions;
